import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { TEST_CHAT_PHONE_NUMBER } from "../../config/config";
import { TestChatContextModel } from "../../data/api/ChatApi";
import { chatManager } from "../../data/managers/ChatManager";
import { MessageModel, MessageTypeEnum } from "../../data/models/Chat/MessageModel";
import { TypeRoleEnum } from "../../data/models/Chat/TypeRoleEnum";
import {
  FirestoreTestChatReplayRunModel,
  TestChatLabelModel,
  TestChatMetadataModel,
  TestChatSessionModel,
  useChatDocument,
  useTestChatReplayRuns,
  useServiceMessages,
  useTestChatSessions,
} from "../../data/providers/FirestoreProvider";
import { useSoulAccess } from "../../data/providers/SoulProvider";
import { useStorageImages } from "../../data/providers/StorageProvider";
import {
  clearApiBaseUrlOverride,
  DEV_API_BASE_URL,
  getConfiguredApiBaseUrl,
  LOCAL_API_BASE_URL,
  PROD_API_BASE_URL,
  setApiBaseUrlOverride,
} from "../../utils/RequestBuilder";

type ApiPreset = "local" | "dev" | "prod" | "custom";

const normalizeUrl = (value: string) => value.trim().replace(/\/$/, "");

const resolveApiPreset = (value: string): ApiPreset => {
  const normalizedValue = normalizeUrl(value);
  if (normalizedValue === LOCAL_API_BASE_URL) return "local";
  if (normalizedValue === DEV_API_BASE_URL) return "dev";
  if (normalizedValue === PROD_API_BASE_URL) return "prod";
  return "custom";
};

const toOptionalDate = (value?: Date | string | null) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const createEmptyTestChatMetadata = (): TestChatMetadataModel => ({
  notes: "",
  labels: [],
  evaluation: {
    rating: 0,
    title: "",
    description: "",
    finalizedAt: null,
  },
  updatedAt: null,
});

const cloneTestChatMetadata = (
  metadata?: Partial<TestChatMetadataModel>
): TestChatMetadataModel => ({
  notes: metadata?.notes || "",
  labels: (metadata?.labels || []).map((label) => ({
    id: label.id,
    name: label.name,
    color: label.color || "#1f6feb",
  })),
  evaluation: {
    rating:
      typeof metadata?.evaluation?.rating === "number"
        ? Math.max(0, Math.min(10, Math.round(metadata.evaluation.rating)))
        : 0,
    title: metadata?.evaluation?.title || "",
    description: metadata?.evaluation?.description || "",
    finalizedAt: toOptionalDate(metadata?.evaluation?.finalizedAt),
  },
  updatedAt: toOptionalDate(metadata?.updatedAt),
});

const getLabelKey = (label: Pick<TestChatLabelModel, "id" | "name">) =>
  (label.id || label.name)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");

const sanitizeTestChatMetadata = (
  metadata: TestChatMetadataModel
): TestChatMetadataModel => {
  const labelsMap = new Map<string, TestChatLabelModel>();

  metadata.labels.forEach((label) => {
    if (!label?.name?.trim()) return;
    const normalizedLabel = {
      id: getLabelKey(label),
      name: label.name.trim(),
      color: label.color || "#1f6feb",
    };
    labelsMap.set(getLabelKey(normalizedLabel), normalizedLabel);
  });

  return {
    notes: metadata.notes.trim(),
    labels: Array.from(labelsMap.values()),
    evaluation: {
      rating: Math.max(0, Math.min(10, Math.round(metadata.evaluation.rating || 0))),
      title: metadata.evaluation.title.trim(),
      description: metadata.evaluation.description.trim(),
      finalizedAt: toOptionalDate(metadata.evaluation.finalizedAt),
    },
    updatedAt: metadata.updatedAt || null,
  };
};

const hasMetadataContent = (metadata: TestChatMetadataModel) =>
  Boolean(
    metadata.notes.trim() ||
      metadata.labels.length ||
      metadata.evaluation.rating ||
      metadata.evaluation.title.trim() ||
      metadata.evaluation.description.trim()
  );

export function useTestChat() {
  const { soul } = useSoulAccess();
  const [companyConfig, setCompanyConfig] = useState<TestChatContextModel>();
  const [configError, setConfigError] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string>();
  const [draftMessages, setDraftMessages] = useState<MessageModel[]>([]);
  const [optimisticMessages, setOptimisticMessages] = useState<MessageModel[]>(
    []
  );
  const [isDraftChat, setIsDraftChat] = useState(false);
  const [awaitingNewSession, setAwaitingNewSession] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [apiBaseUrl, setApiBaseUrl] = useState(getConfiguredApiBaseUrl());
  const [apiInputValue, setApiInputValue] = useState(getConfiguredApiBaseUrl());
  const [draftSessionMetadata, setDraftSessionMetadata] = useState(
    createEmptyTestChatMetadata()
  );
  const [sessionMetadata, setSessionMetadata] = useState(
    createEmptyTestChatMetadata()
  );
  const [metadataDirty, setMetadataDirty] = useState(false);
  const [isSavingMetadata, setIsSavingMetadata] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [metadataStatusMessage, setMetadataStatusMessage] = useState<
    string | null
  >(null);
  const [metadataSavedAt, setMetadataSavedAt] = useState<Date | null>(null);
  const [replayIterations, setReplayIterations] = useState(1);
  const [isRunningReplay, setIsRunningReplay] = useState(false);
  const [replayError, setReplayError] = useState<string | null>(null);
  const [selectedReplayRunId, setSelectedReplayRunId] = useState<
    string | null
  >(null);
  const endOfMessagesRef = useRef<HTMLDivElement>(null);
  const lastHydratedSessionIdRef = useRef<string | null>(null);
  const { uploadFileAndGetUrl } = useStorageImages();

  useEffect(() => {
    const loadContext = async () => {
      if (!soul) {
        setCompanyConfig(undefined);
        setConfigError(null);
        return;
      }

      try {
        setConfigError(null);
        const config = await chatManager.getTestChatContext();
        if (!config) {
          setCompanyConfig(undefined);
          setConfigError("No se pudo cargar la configuración.");
          return;
        }
        setCompanyConfig(config);
      } catch (error: any) {
        setCompanyConfig(undefined);
        setConfigError(error?.message || "No se pudo cargar la configuración.");
      }
    };

    loadContext();
  }, [soul]);

  const userData = soul;
  const company = userData?.company;

  const {
    sessions,
    loading: loadingSessions,
    error: sessionsError,
  } = useTestChatSessions(company!, 25);
  const {
    chat: testChatUser,
    loading: loadingChatDocument,
    error: chatDocumentError,
  } = useChatDocument(company!, TEST_CHAT_PHONE_NUMBER);

  const activeServiceId = testChatUser?.serviceActive?.trim() || undefined;
  const selectedServiceId = isDraftChat ? undefined : selectedSessionId;

  const {
    messages: liveMessages,
    loading: loadingMessages,
    error: messagesError,
  } = useServiceMessages(company!, selectedServiceId);
  const {
    runs: replayRuns,
    loading: loadingReplayRuns,
    error: replayRunsError,
  } = useTestChatReplayRuns(
    company!,
    isDraftChat ? undefined : selectedSessionId,
    12
  );

  const selectedReplayRun = useMemo(
    () => replayRuns.find((run) => run.id === selectedReplayRunId),
    [replayRuns, selectedReplayRunId]
  );

  const {
    messages: replayMessages,
    loading: loadingReplayMessages,
    error: replayMessagesError,
  } = useServiceMessages(company!, selectedReplayRun?.replayServiceCode);

  useEffect(() => {
    if (!sessions.length) {
      if (!isDraftChat) {
        setSelectedSessionId(undefined);
      }
      return;
    }

    if (isDraftChat) return;

    const hasSelectedSession = selectedSessionId
      ? sessions.some((session) => session.id === selectedSessionId)
      : false;

    if (hasSelectedSession) return;

    if (activeServiceId) {
      const activeSessionExists = sessions.some(
        (session) => session.id === activeServiceId
      );
      if (activeSessionExists) {
        setSelectedSessionId(activeServiceId);
        return;
      }
    }

    setSelectedSessionId(sessions[0].id);
  }, [activeServiceId, isDraftChat, selectedSessionId, sessions]);

  const selectedSession = useMemo(
    () => sessions.find((session) => session.id === selectedSessionId),
    [selectedSessionId, sessions]
  );

  useEffect(() => {
    setSelectedReplayRunId(null);
    setReplayError(null);
  }, [isDraftChat, selectedSessionId]);

  useEffect(() => {
    if (!selectedSession || isDraftChat) return;
    if (lastHydratedSessionIdRef.current === selectedSession.id) return;

    lastHydratedSessionIdRef.current = selectedSession.id;
    setSessionMetadata(cloneTestChatMetadata(selectedSession.testMetadata));
    setMetadataDirty(false);
    setMetadataError(null);
    setMetadataStatusMessage(null);
    setMetadataSavedAt(selectedSession.testMetadata.updatedAt || null);
  }, [isDraftChat, selectedSession]);

  const persistSessionMetadata = useCallback(
    async (
      serviceCode: string,
      metadata: TestChatMetadataModel,
      successMessage: string
    ) => {
      const normalizedMetadata = sanitizeTestChatMetadata(metadata);
      setSessionMetadata(normalizedMetadata);
      setIsSavingMetadata(true);
      setMetadataError(null);
      try {
        const isSuccess = await chatManager.updateTestSessionMetadata({
          serviceCode,
          metadata: {
            notes: normalizedMetadata.notes,
            labels: normalizedMetadata.labels,
            evaluation: {
              ...normalizedMetadata.evaluation,
              finalizedAt: normalizedMetadata.evaluation.finalizedAt
                ? normalizedMetadata.evaluation.finalizedAt.toISOString()
                : null,
            },
          },
        });

        if (!isSuccess) {
          setMetadataError("No se pudo guardar el mapeo de la sesión.");
          return false;
        }

        setMetadataDirty(false);
        setMetadataSavedAt(new Date());
        setMetadataStatusMessage(successMessage);
        return true;
      } finally {
        setIsSavingMetadata(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!awaitingNewSession || !sessions.length) return;

    const nextSessionId =
      (activeServiceId &&
        sessions.find((session) => session.id === activeServiceId)?.id) ||
      sessions[0]?.id;

    if (!nextSessionId) return;

    const pendingDraftMetadata = cloneTestChatMetadata(draftSessionMetadata);

    setSelectedSessionId(nextSessionId);
    setIsDraftChat(false);
    setAwaitingNewSession(false);
    lastHydratedSessionIdRef.current = nextSessionId;

    if (hasMetadataContent(pendingDraftMetadata)) {
      setSessionMetadata(pendingDraftMetadata);
      setMetadataDirty(true);
      setMetadataSavedAt(null);
      void persistSessionMetadata(
        nextSessionId,
        pendingDraftMetadata,
        "Mapeo guardado en la nueva sesión."
      );
    } else {
      setSessionMetadata(createEmptyTestChatMetadata());
      setMetadataDirty(false);
      setMetadataSavedAt(null);
    }

    setDraftSessionMetadata(createEmptyTestChatMetadata());
  }, [
    activeServiceId,
    awaitingNewSession,
    draftSessionMetadata,
    persistSessionMetadata,
    sessions,
  ]);

  useEffect(() => {
    if (!isDraftChat) {
      setOptimisticMessages([]);
    }
  }, [isDraftChat, liveMessages]);

  const displayedMessages = isDraftChat
    ? draftMessages
    : [...liveMessages, ...optimisticMessages];
  const visibleMessages = selectedReplayRun ? replayMessages : displayedMessages;

  const canSendMessages =
    !selectedReplayRun &&
    (isDraftChat ||
      (!selectedSessionId && !sessions.length) ||
      selectedSessionId === activeServiceId);

  const canEditSessionMetadata = isDraftChat || Boolean(selectedSession);
  const currentApiPreset = resolveApiPreset(apiBaseUrl);

  const scrollToBottom = useCallback(() => {
    const container = endOfMessagesRef.current?.parentElement;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom, visibleMessages]);

  const updateSessionMetadata = useCallback(
    (updater: (prev: TestChatMetadataModel) => TestChatMetadataModel) => {
      setMetadataError(null);
      setMetadataStatusMessage(null);
      setMetadataSavedAt(null);
      setMetadataDirty(true);
      setSessionMetadata((prev) => {
        const nextMetadata = cloneTestChatMetadata(updater(prev));
        if (isDraftChat) {
          setDraftSessionMetadata(nextMetadata);
        }
        return nextMetadata;
      });
    },
    [isDraftChat]
  );

  const setSessionNotes = (notes: string) => {
    updateSessionMetadata((prev) => ({
      ...prev,
      notes,
    }));
  };

  const setSessionEvaluationRating = (rating: number) => {
    updateSessionMetadata((prev) => ({
      ...prev,
      evaluation: {
        ...prev.evaluation,
        rating,
      },
    }));
  };

  const setSessionEvaluationTitle = (title: string) => {
    updateSessionMetadata((prev) => ({
      ...prev,
      evaluation: {
        ...prev.evaluation,
        title,
      },
    }));
  };

  const setSessionEvaluationDescription = (description: string) => {
    updateSessionMetadata((prev) => ({
      ...prev,
      evaluation: {
        ...prev.evaluation,
        description,
      },
    }));
  };

  const addSessionLabel = (label: TestChatLabelModel) => {
    updateSessionMetadata((prev) => {
      const nextLabel = {
        id: getLabelKey(label),
        name: label.name.trim(),
        color: label.color || "#1f6feb",
      };
      const exists = prev.labels.some(
        (item) => getLabelKey(item) === getLabelKey(nextLabel)
      );
      if (exists) return prev;
      return {
        ...prev,
        labels: [...prev.labels, nextLabel],
      };
    });
  };

  const toggleSessionLabel = (label: TestChatLabelModel) => {
    const normalizedLabel = {
      id: getLabelKey(label),
      name: label.name.trim(),
      color: label.color || "#1f6feb",
    };

    updateSessionMetadata((prev) => {
      const exists = prev.labels.some(
        (item) => getLabelKey(item) === getLabelKey(normalizedLabel)
      );
      return {
        ...prev,
        labels: exists
          ? prev.labels.filter(
              (item) => getLabelKey(item) !== getLabelKey(normalizedLabel)
            )
          : [...prev.labels, normalizedLabel],
      };
    });
  };

  const removeSessionLabel = (labelId: string) => {
    updateSessionMetadata((prev) => ({
      ...prev,
      labels: prev.labels.filter((label) => label.id !== labelId),
    }));
  };

  const saveSessionMetadata = async ({
    finalize = false,
  }: {
    finalize?: boolean;
  } = {}) => {
    const nextMetadata = sanitizeTestChatMetadata({
      ...sessionMetadata,
      evaluation: {
        ...sessionMetadata.evaluation,
        finalizedAt: finalize
          ? new Date()
          : toOptionalDate(sessionMetadata.evaluation.finalizedAt),
      },
    });

    setSessionMetadata(nextMetadata);

    if (isDraftChat) {
      setDraftSessionMetadata(nextMetadata);
      setMetadataDirty(false);
      setMetadataSavedAt(new Date());
      setMetadataStatusMessage(
        finalize
          ? "La evaluación se guardará cuando envíes el primer mensaje."
          : "Notas y etiquetas listas para guardarse en la próxima sesión."
      );
      return true;
    }

    if (!selectedSession?.id) {
      setMetadataError(
        "Envía el primer mensaje o abre una sesión para guardar el mapeo."
      );
      return false;
    }

    return persistSessionMetadata(
      selectedSession.id,
      nextMetadata,
      finalize ? "Evaluación finalizada." : "Mapeo guardado."
    );
  };

  const selectSession = (session: TestChatSessionModel) => {
    setIsDraftChat(false);
    setDraftMessages([]);
    setOptimisticMessages([]);
    setSelectedSessionId(session.id);
    lastHydratedSessionIdRef.current = session.id;
    setSessionMetadata(cloneTestChatMetadata(session.testMetadata));
    setMetadataDirty(false);
    setMetadataError(null);
    setMetadataStatusMessage(null);
    setMetadataSavedAt(session.testMetadata.updatedAt || null);
  };

  const startNewChat = async () => {
    setIsResetting(true);
    try {
      const isSuccess = await chatManager.resetTestChat(false);
      if (!isSuccess) return false;
      const emptyMetadata = createEmptyTestChatMetadata();
      setIsDraftChat(true);
      setAwaitingNewSession(false);
      setSelectedSessionId(undefined);
      setDraftMessages([]);
      setOptimisticMessages([]);
      setDraftSessionMetadata(emptyMetadata);
      setSessionMetadata(emptyMetadata);
      setMetadataDirty(false);
      setMetadataError(null);
      setMetadataStatusMessage(null);
      setMetadataSavedAt(null);
      lastHydratedSessionIdRef.current = "__draft__";
      return true;
    } finally {
      setIsResetting(false);
    }
  };

  const clearHistory = async () => {
    setIsResetting(true);
    try {
      const isSuccess = await chatManager.resetTestChat(true);
      if (!isSuccess) return false;
      const emptyMetadata = createEmptyTestChatMetadata();
      setIsDraftChat(true);
      setAwaitingNewSession(false);
      setSelectedSessionId(undefined);
      setDraftMessages([]);
      setOptimisticMessages([]);
      setDraftSessionMetadata(emptyMetadata);
      setSessionMetadata(emptyMetadata);
      setMetadataDirty(false);
      setMetadataError(null);
      setMetadataStatusMessage(null);
      setMetadataSavedAt(null);
      lastHydratedSessionIdRef.current = "__draft__";
      return true;
    } finally {
      setIsResetting(false);
    }
  };

  const selectReplayRun = (
    run: FirestoreTestChatReplayRunModel | null | undefined
  ) => {
    setSelectedReplayRunId(run?.id || null);
    setReplayError(null);
  };

  const clearSelectedReplayRun = () => {
    setSelectedReplayRunId(null);
  };

  const runSessionReplay = async () => {
    if (!selectedSession?.id || isDraftChat) {
      setReplayError("Abre una sesión histórica para ejecutar el replay.");
      return false;
    }

    setIsRunningReplay(true);
    setReplayError(null);

    try {
      const result = await chatManager.replayTestSession({
        serviceCode: selectedSession.id,
        iterations: replayIterations,
      });
      const replayRuns = result.data?.runs ?? [];

      if (!replayRuns.length) {
        const errorMessage =
          result.error || "No se pudo ejecutar el replay de la sesión.";
        const deployHint =
          currentApiPreset !== "local" &&
          /\/chat\/test\/session\/replay|devolvio html|404|not found/i.test(
            errorMessage
          )
            ? " Cambia la API a Local o despliega esta ruta en DEV/PROD."
            : "";
        setReplayError(`${errorMessage}${deployHint}`);
        return false;
      }

      const newestRun = [...replayRuns].sort((a, b) => {
        const left = new Date(a.completedAt).getTime();
        const right = new Date(b.completedAt).getTime();
        return right - left;
      })[0];

      if (newestRun?.id) {
        setSelectedReplayRunId(newestRun.id);
      }

      return true;
    } finally {
      setIsRunningReplay(false);
    }
  };

  const sendMessage = async (
    options:
      | { type: MessageTypeEnum.TEXT; content: string }
      | { type: MessageTypeEnum.IMAGE; file: File }
      | { type: MessageTypeEnum.AUDIO; blob: Blob }
  ) => {
    let content = "";
    let metadata: any = undefined;

    if (options.type === MessageTypeEnum.TEXT) {
      content = options.content;
    }

    if (options.type === MessageTypeEnum.IMAGE) {
      content = URL.createObjectURL(options.file);
      metadata = {
        fileName: options.file.name,
        size: options.file.size,
        mimeType: options.file.type,
      };
    }

    if (options.type === MessageTypeEnum.AUDIO) {
      content = URL.createObjectURL(options.blob);
      metadata = {
        fileName: `voice-${Date.now()}.webm`,
        size: options.blob.size,
        mimeType: options.blob.type || "audio/webm",
      };
    }

    const tempMessage = new MessageModel({
      id: uuidv4(),
      content,
      role: TypeRoleEnum.USER,
      createdAt: new Date(),
      messageType: options.type,
      status: "pending",
      contentMetadata: metadata ? JSON.stringify(metadata) : undefined,
    });

    if (isDraftChat) {
      setDraftMessages((prev) => [...prev, tempMessage]);
    } else {
      setOptimisticMessages((prev) => [...prev, tempMessage]);
    }

    try {
      if (options.type === MessageTypeEnum.IMAGE) {
        content = await uploadFileAndGetUrl(
          options.file,
          TEST_CHAT_PHONE_NUMBER,
          "image"
        );
      }

      if (options.type === MessageTypeEnum.AUDIO) {
        const audioFile = new File([options.blob], `voice-${Date.now()}.webm`, {
          type: options.blob.type || "audio/webm",
        });

        content = await uploadFileAndGetUrl(
          audioFile,
          TEST_CHAT_PHONE_NUMBER,
          "audio"
        );
      }

      const isSuccess = await chatManager.sendTestMessage({
        type: options.type,
        message: content,
      });

      if (!isSuccess) {
        throw new Error("No se pudo enviar el mensaje de prueba.");
      }

      if (isDraftChat) {
        setAwaitingNewSession(true);
      }
    } catch (error) {
      if (isDraftChat) {
        setDraftMessages((prev) =>
          prev.map((message) =>
            message.id === tempMessage.id
              ? new MessageModel({ ...message, status: "failed" })
              : message
          )
        );
      } else {
        setOptimisticMessages((prev) =>
          prev.map((message) =>
            message.id === tempMessage.id
              ? new MessageModel({ ...message, status: "failed" })
              : message
          )
        );
      }
    }
  };

  const saveApiBaseUrl = () => {
    const normalizedValue = normalizeUrl(apiInputValue);
    if (!normalizedValue) {
      clearApiBaseUrlOverride();
      const nextUrl = getConfiguredApiBaseUrl();
      setApiBaseUrl(nextUrl);
      setApiInputValue(nextUrl);
      return;
    }

    setApiBaseUrlOverride(normalizedValue);
    setApiBaseUrl(normalizedValue);
    setApiInputValue(normalizedValue);
  };

  const applyApiPreset = (value: string) => {
    setApiInputValue(value);
    setApiBaseUrlOverride(value);
    setApiBaseUrl(value);
  };

  const resetApiBaseUrl = () => {
    clearApiBaseUrlOverride();
    const nextUrl = getConfiguredApiBaseUrl();
    setApiBaseUrl(nextUrl);
    setApiInputValue(nextUrl);
  };

  const openSettings = () => {
    setApiInputValue(apiBaseUrl);
    setSettingsOpen(true);
  };

  const openMapping = () => {
    setMappingOpen(true);
  };

  return {
    activeServiceId,
    addSessionLabel,
    apiBaseUrl,
    apiInputValue,
    applyApiPreset,
    apiPresets: {
      local: LOCAL_API_BASE_URL,
      dev: DEV_API_BASE_URL,
      prod: PROD_API_BASE_URL,
    },
    canEditSessionMetadata,
    canSendMessages,
    chatDocumentError,
    clearHistory,
    clearSelectedReplayRun,
    company,
    companyConfig,
    configError,
    currentApiPreset,
    displayedMessages: visibleMessages,
    endOfMessagesRef,
    isDraftChat,
    isRunningReplay,
    isResetting,
    isSavingMetadata,
    loading:
      loadingSessions ||
      loadingMessages ||
      loadingChatDocument ||
      loadingReplayRuns ||
      (selectedReplayRun ? loadingReplayMessages : false),
    mappingOpen,
    messagesError,
    metadataDirty,
    metadataError,
    metadataSavedAt,
    metadataStatusMessage,
    openMapping,
    openSettings,
    removeSessionLabel,
    replayError,
    replayIterations,
    replayMessagesError,
    replayRuns,
    replayRunsError,
    resetApiBaseUrl,
    runSessionReplay,
    saveApiBaseUrl,
    saveSessionMetadata,
    selectedServiceId,
    selectedReplayRun,
    selectedSession,
    selectReplayRun,
    selectSession,
    sessionMetadata,
    sessions,
    sessionsError,
    setApiInputValue,
    setMappingOpen,
    setReplayIterations,
    setSessionEvaluationDescription,
    setSessionEvaluationRating,
    setSessionEvaluationTitle,
    setSessionNotes,
    setSettingsOpen,
    settingsOpen,
    startNewChat,
    testPhoneNumber: TEST_CHAT_PHONE_NUMBER,
    toggleSessionLabel,
    sendMessage,
  };
}
